@import "tailwindcss";

@theme {
  --font-sans: "Space Grotesk", "Segoe UI", sans-serif;

  --color-euphoria-ink: #f4f1ed;
  --color-euphoria-ink-muted: rgba(244, 241, 237, 0.72);
  --color-euphoria-accent: #b089ff;
  --color-euphoria-accent-dark: #9a6bff;
  --color-euphoria-cream: #14181f;
  --color-euphoria-mist: rgba(22, 26, 33, 0.9);
  --color-euphoria-border: rgba(244, 241, 237, 0.12);
  --color-euphoria-base: #0b0d12;
  --color-euphoria-panel: #11141b;
  --color-euphoria-card: #141a22;

  --shadow-euphoria: 0 30px 80px rgba(0, 0, 0, 0.45);
  --shadow-euphoria-card: 0 18px 40px rgba(0, 0, 0, 0.35);
  --shadow-euphoria-card-hover: 0 22px 44px rgba(0, 0, 0, 0.4);
}

:root {
  --ink: var(--color-euphoria-ink);
  --accent: var(--color-euphoria-accent);
  --accent-dark: var(--color-euphoria-accent-dark);
  --cream: var(--color-euphoria-cream);
  --mist: var(--color-euphoria-mist);
  --ink-70: var(--color-euphoria-ink-muted);
  --border: var(--color-euphoria-border);
  --shadow: var(--shadow-euphoria);
}

@layer base {
  html {
    scroll-behavior: smooth;
  }

  * {
    @apply m-0 box-border p-0;
  }

  body {
    @apply overflow-x-hidden font-sans text-euphoria-ink;
    background:
      radial-gradient(circle at 20% 10%, #1c222d 0, #14181f 40%, #0b0d12 100%),
      radial-gradient(
        circle at 80% 80%,
        rgba(154, 107, 255, 0.12),
        transparent 55%
      );
    line-height: 1.6;
  }

  a {
    @apply text-inherit no-underline;
  }

  img {
    @apply block w-full rounded-[20px];
  }
}

.topbar {
  @apply sticky top-0 z-10 flex items-center justify-between border-b border-euphoria-border;
  padding: 20px 6vw;
  background: rgba(13, 16, 21, 0.92);
  backdrop-filter: blur(14px);
}

.brand {
  @apply flex items-center font-bold;
  gap: 12px;
  letter-spacing: 0.5px;
}

.brand img {
  width: 34px;
  height: 34px;
  border-radius: 8px;
}

.nav {
  @apply flex font-medium;
  gap: 26px;
}

.nav a {
  @apply relative;
  padding-bottom: 4px;
}

.nav a::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: 0;
  height: 2px;
  width: 0;
  background: var(--accent);
  transition: width 0.2s ease;
}

.nav a:hover::after {
  width: 100%;
}

.cta-group {
  @apply flex;
  gap: 12px;
}

.solid,
.ghost {
  @apply rounded-full font-semibold;
  padding: 10px 18px;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.solid {
  background: var(--color-euphoria-accent);
  color: var(--color-euphoria-base);
  box-shadow: 0 12px 30px rgba(176, 137, 255, 0.3);
}

.ghost {
  border: 1px solid var(--ink);
  background: transparent;
}

.solid:hover,
.ghost:hover {
  transform: translateY(-2px);
}

.hero {
  @apply grid items-center;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;
  padding: 80px 6vw 60px;
}

.hero-copy h1 {
  font-size: clamp(2.6rem, 3.5vw, 4rem);
  line-height: 1.05;
  margin-bottom: 20px;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 0.75rem;
  color: var(--accent);
  margin-bottom: 16px;
}

.lede {
  font-size: 1.1rem;
  color: var(--ink-70);
  max-width: 500px;
}

.hero-actions {
  @apply flex flex-wrap;
  gap: 14px;
  margin: 28px 0;
}

.hero-stats {
  @apply flex flex-wrap font-semibold;
  gap: 30px;
}

.stat {
  display: block;
  font-size: 1.1rem;
}

.label {
  font-size: 0.85rem;
  color: var(--ink-70);
}

.hero-media {
  @apply relative;
}

.hero-media img {
  box-shadow: var(--shadow);
}

.badge {
  @apply absolute rounded-full font-semibold;
  bottom: -16px;
  left: 20px;
  background: var(--color-euphoria-cream);
  padding: 12px 18px;
  border: 1px solid var(--border);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.4);
}

section {
  padding: 70px 6vw;
}

.section-title {
  @apply grid;
  gap: 12px;
  margin-bottom: 40px;
}

.section-title h2 {
  letter-spacing: -0.02em;
}

.section-title h2 {
  font-size: clamp(2rem, 2.5vw, 2.8rem);
}

.section-title p {
  font-size: 1rem;
  max-width: 600px;
  color: var(--ink-70);
}

@media (max-width: 1200px) {
  section {
    padding: 64px 5vw;
  }
}

@media (max-width: 900px) {
  section {
    padding: 56px 5vw;
  }

  .hero {
    gap: 28px;
    padding: 56px 6vw 44px;
  }
}

@media (max-width: 640px) {
  section {
    padding: 48px 6vw;
  }

  .hero-actions {
    gap: 10px;
    margin: 20px 0;
  }
}

.feature-grid,
.product-grid,
.community-grid {
  @apply grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

@media (max-width: 900px) {
  .feature-grid,
  .product-grid,
  .community-grid {
    gap: 18px;
  }
}

.community-split {
  @apply grid items-start;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  margin-bottom: 24px;
}

@media (max-width: 900px) {
  .community-split {
    grid-template-columns: 1fr;
  }
}

.community-block h3 {
  margin-bottom: 12px;
}

.community-block .data-grid {
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

#contributors-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 720px) {
  #contributors-grid {
    grid-template-columns: 1fr;
  }
}

.data-grid {
  @apply grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

@media (max-width: 900px) {
  .data-grid {
    grid-template-columns: 1fr;
  }
}

.data-card {
  @apply grid;
  padding: 18px;
  border-radius: 16px;
  background: var(--color-euphoria-card);
  border: 1px solid var(--border);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.3);
  gap: 12px;
  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.data-card:hover {
  transform: translateY(-2px);
  border-color: rgba(176, 137, 255, 0.35);
  box-shadow: 0 22px 44px rgba(0, 0, 0, 0.4);
}

.product-card img {
  width: 100%;
  border-radius: 12px;
  border: 1px solid var(--border);
}

.product-image {
  @apply relative;
}

.version-badge {
  @apply absolute rounded-full font-semibold;
  top: 10px;
  left: 10px;
  padding: 4px 10px;
  background: rgba(11, 13, 18, 0.7);
  border: 1px solid rgba(176, 137, 255, 0.4);
  color: var(--ink);
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  backdrop-filter: blur(8px);
}

.data-card .title-row {
  @apply flex items-start justify-between;
  gap: 8px;
}

.person-row {
  @apply flex items-center;
  gap: 12px;
}

.person-card .title-row {
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-start;
}

.person-card .title-row .pill {
  margin-left: 6px;
}

.person-card h4 {
  font-size: 1rem;
  line-height: 1.2;
  margin: 0;
}

.person-card p {
  margin-top: 4px;
  margin-bottom: 0;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.person-card .pill {
  font-size: 0.7rem;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.person-card {
  display: block;
}

.avatar {
  @apply shrink-0 rounded-full object-cover;
  width: 48px;
  height: 48px;
  border: 1px solid var(--border);
}

.data-link {
  @apply inline-flex items-center justify-center rounded-full font-semibold;
  padding: 8px 12px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--ink);
  font-size: 0.85rem;
  transition:
    transform 0.2s ease,
    background 0.2s ease;
}

.data-link:hover {
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.08);
}

.data-link.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.data-card.loading {
  color: var(--ink-70);
  text-align: center;
}

.data-card .meta {
  @apply flex flex-wrap items-center;
  gap: 8px;
  font-size: 0.8rem;
  color: var(--ink-70);
}

.data-card .pill {
  @apply inline-flex items-center rounded-full;
  line-height: 1;
  padding: 6px 10px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.02);
  min-height: 26px;
}

.data-card .actions {
  @apply flex flex-wrap;
  gap: 8px;
}

.product-block {
  margin-top: 28px;
}

.product-block-title {
  @apply flex items-center justify-between font-semibold;
  gap: 16px;
  margin-bottom: 14px;
}

.carousel-controls {
  @apply flex;
  gap: 8px;
}

.carousel-btn {
  @apply cursor-pointer rounded-full;
  width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--ink);
  font-size: 1.2rem;
  line-height: 1;
  transition:
    transform 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;
}

.carousel-btn:hover {
  transform: translateY(-1px);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(176, 137, 255, 0.5);
}

.carousel-track {
  @apply overflow-x-auto overflow-y-hidden;
  padding-bottom: 8px;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
}

.carousel-track::-webkit-scrollbar {
  height: 8px;
}

.carousel-track::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 999px;
}

.carousel-grid {
  @apply flex;
  gap: 16px;
}

.carousel-grid .data-card {
  flex: 0 0 calc((100% - 64px) / 5);
  min-width: 0;
  max-width: none;
  scroll-snap-align: start;
}

@media (max-width: 1200px) {
  .carousel-grid .data-card {
    flex: 0 0 calc((100% - 48px) / 4);
  }
}

@media (max-width: 1024px) {
  .carousel-grid .data-card {
    flex: 0 0 calc((100% - 32px) / 3);
  }
}

@media (max-width: 900px) {
  .carousel-grid .data-card {
    flex: 0 0 calc((100% - 16px) / 2);
  }
}

@media (max-width: 560px) {
  .carousel-grid .data-card {
    flex: 0 0 100%;
  }
}

.feature-grid article {
  padding: 24px;
  border-radius: 18px;
  background: var(--mist);
  border: 1px solid var(--border);
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
}

.reveal-base {
  opacity: 0;
  transform: translateY(24px);
  transition:
    opacity 0.6s ease,
    transform 0.6s ease;
}

.reveal {
  opacity: 1;
  transform: translateY(0);
}

.gallery-grid {
  @apply grid;
  gap: 20px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.video-frame {
  @apply relative overflow-hidden;
  padding-top: 56.25%;
  border-radius: 20px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
}

.video-frame iframe {
  @apply absolute inset-0;
  width: 100%;
  height: 100%;
  border: 0;
}

.video-frame lite-youtube {
  @apply absolute inset-0;
  width: 100%;
  height: 100%;
  max-width: none;
}

.gallery-grid img {
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
  cursor: pointer;
}

.gallery-grid img:hover {
  transform: translateY(-4px);
  box-shadow: 0 22px 44px rgba(0, 0, 0, 0.45);
}

.image-modal {
  @apply pointer-events-none fixed inset-0 z-50 flex items-center justify-center;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.image-modal.is-open {
  opacity: 1;
  pointer-events: auto;
}

.image-modal-backdrop {
  @apply absolute inset-0;
  background: rgba(7, 9, 14, 0.75);
  backdrop-filter: blur(6px);
}

.image-modal-content {
  @apply relative z-1 grid;
  max-width: min(1100px, 90vw);
  max-height: 85vh;
  gap: 12px;
  padding: 16px;
  border-radius: 18px;
  background: #11141b;
  border: 1px solid var(--border);
  box-shadow: var(--shadow);
}

.image-modal-content img {
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 14px;
}

.image-modal-content p {
  color: var(--ink-70);
  text-align: center;
}

.image-modal-close {
  @apply absolute cursor-pointer rounded-full;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  border: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.06);
  color: var(--ink);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
}

.products {
  background: var(--color-euphoria-panel);
}

.product-grid article {
  @apply grid overflow-hidden;
  border-radius: 22px;
  background: #171b23;
  box-shadow: var(--shadow);
  grid-template-rows: 1fr auto;
  border: 1px solid var(--border);
}

.product-body {
  padding: 22px;
}

.product-body p {
  color: var(--ink-70);
  margin: 12px 0 20px;
}

.product-meta {
  @apply flex items-center justify-between font-semibold;
  gap: 12px;
}

.product-meta .links {
  @apply flex;
  gap: 12px;
  font-size: 0.9rem;
}

.community {
  background: linear-gradient(120deg, #0e1218, #171b23);
}

.card {
  padding: 24px;
  border-radius: 18px;
  border: 1px solid var(--border);
  background: #151a22;
  min-height: 220px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
}

.card ul {
  @apply grid list-none;
  list-style: none;
  margin-top: 16px;
  gap: 10px;
}

.card a {
  color: var(--accent-dark);
  font-weight: 600;
}

.about {
  background: var(--color-euphoria-base);
  color: #fef7f0;
}

.about-inner {
  @apply grid items-center;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.about-links {
  @apply flex flex-wrap;
  gap: 14px;
}

.about-links a {
  @apply rounded-full font-semibold;
  background: #fef7f0;
  color: var(--color-euphoria-base);
  padding: 10px 16px;
}

.footer {
  @apply flex flex-wrap justify-between border-t border-euphoria-border;
  padding: 30px 6vw 50px;
  gap: 12px;
  background: var(--color-euphoria-base);
}

.footer-links {
  @apply flex font-semibold;
  gap: 16px;
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(0px);
  opacity: 0.7;
  z-index: -2;
}

.orb-1 {
  width: 380px;
  height: 380px;
  background: radial-gradient(
    circle,
    rgba(176, 137, 255, 0.42),
    transparent 70%
  );
  top: -120px;
  right: -80px;
}

.orb-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(
    circle,
    rgba(76, 108, 255, 0.25),
    transparent 70%
  );
  bottom: 10vh;
  left: -120px;
}

.grid-overlay {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 90px 90px;
  z-index: -3;
  opacity: 0.25;
}

@media (max-width: 900px) {
  .nav {
    display: none;
  }

  .community-block .data-grid {
    grid-template-columns: 1fr;
  }

  .topbar {
    flex-wrap: wrap;
    gap: 14px;
  }

  .hero {
    padding-top: 50px;
  }
}

@media (max-width: 640px) {
  .cta-group {
    width: 100%;
    justify-content: flex-start;
  }

  .hero-stats {
    flex-direction: column;
    gap: 12px;
  }

  .product-meta {
    flex-direction: column;
    align-items: flex-start;
  }
}
